import { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { CourseGeometry, GeometryResponse } from '../lib/golfRound'

/** What a course's traced geometry came back as — stamped with the key it answers. */
interface GeometryResult {
  key: string
  geometry: CourseGeometry | null
  unmapped: boolean
  error: string | null
}

export interface CourseGeometryBook {
  geometry: CourseGeometry | null
  /** The honest half of a 404: nobody has traced this course. NOT an error. */
  unmapped: boolean
  isLoading: boolean
  error: string | null
}

/**
 * One course's traced geometry — `GET /golf/courses/:key/geometry`.
 *
 * ## `unmapped` is an answer, not a failure
 *
 * SEVEN of the club's fifteen courses have no published geometry, and the
 * endpoint 404s (`not_mapped`) rather than serving an empty document precisely
 * so the two can be told apart. A member looking at a round played somewhere
 * nobody has traced should read *"the club has not mapped this one"*, not
 * *"something went wrong"* — so this hook keeps them separate and every caller
 * renders the honest line instead of a plan.
 *
 * ## There is NO client cache here, deliberately
 *
 * The endpoint is `fresh_when(etag: checksum, public: true)`, so the BROWSER
 * already holds this: a second read of the same course is a conditional
 * request that comes back 304 off the HTTP cache, revalidated for free and
 * invalidated the instant somebody republishes the course. A `Map` in module
 * scope would be a second cache with no invalidation at all — it would serve a
 * stale trace for the life of the tab, and it would have to be reasoned about
 * every time the geometry pipeline changes. The one the platform maintains is
 * strictly better; adding another would be work that makes the page wrong.
 *
 * A null `courseKey` fetches nothing — the round has to land before its course
 * is known, and a request for `/golf/courses/null/geometry` is a 404 nobody
 * asked for.
 */
export function useCourseGeometry(courseKey: string | null): CourseGeometryBook {
  const [result, setResult] = useState<GeometryResult | null>(null)

  useEffect(() => {
    if (courseKey === null) return
    let alive = true
    void api
      .get<GeometryResponse>(`/golf/courses/${courseKey}/geometry`)
      .then((payload) => {
        if (alive) setResult({ key: courseKey, geometry: payload.geometry, unmapped: false, error: null })
      })
      .catch((e: unknown) => {
        if (!alive) return
        const unmapped = e instanceof ApiError && e.status === 404
        setResult({
          key: courseKey,
          geometry: null,
          unmapped,
          error: unmapped ? null : e instanceof Error ? e.message : 'Could not read that course’s plan.',
        })
      })
    return () => {
      alive = false
    }
  }, [courseKey])

  // Derived, never stored: an answer counts only for the course it was asked
  // about, so a page that switched courses reads as loading again rather than
  // drawing the previous course's holes under the new name.
  const settled = result !== null && result.key === courseKey ? result : null

  return {
    geometry: settled?.geometry ?? null,
    unmapped: settled?.unmapped ?? false,
    isLoading: courseKey !== null && settled === null,
    error: settled?.error ?? null,
  }
}
